import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ROUTES } from '../../../constants/routes';
import {
  addWishlistItem,
  removeWishlistByCourseId,
} from '../../../services/wishlists';

const INSTRUCTOR_AVATAR =
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face';

const STAR_PATH =
  'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';

function Star({ fill }) {
  const amount = Math.min(1, Math.max(0, fill));

  if (amount <= 0) {
    return (
      <svg className="h-3.5 w-3.5 text-gray-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path d={STAR_PATH} />
      </svg>
    );
  }

  if (amount >= 1) {
    return (
      <svg className="h-3.5 w-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path d={STAR_PATH} />
      </svg>
    );
  }

  return (
    <span className="relative inline-block h-3.5 w-3.5 shrink-0">
      <svg
        className="absolute inset-0 h-3.5 w-3.5 text-gray-300"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden
      >
        <path d={STAR_PATH} />
      </svg>
      <span className="absolute inset-0 overflow-hidden" style={{ width: `${amount * 100}%` }}>
        <svg className="h-3.5 w-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
          <path d={STAR_PATH} />
        </svg>
      </span>
    </span>
  );
}

function StarRating({ rating }) {
  const value = Math.min(5, Math.max(0, Number(rating) || 0));
  const display = Number.isInteger(value) ? String(value) : value.toFixed(1);

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-sm font-semibold text-[#666666]">{display}</span>
      <div className="flex gap-0.5" aria-label={`${display} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} fill={Math.min(1, Math.max(0, value - (star - 1)))} />
        ))}
      </div>
    </div>
  );
}

function CourseCard({ course, wishlistId, onFavoriteChange, onToggleFavorite, hoverEnroll = false }) {
  const { isLoggedIn, openAuthPopup } = useAuthContext();
  const [fav, setFav] = useState(Boolean(course.isFavorite));
  const [favLoading, setFavLoading] = useState(false);
  const [panelBounce, setPanelBounce] = useState(null);
  const instructorAvatar = course.instructorImage || INSTRUCTOR_AVATAR;

  useEffect(() => {
    setFav(Boolean(course.isFavorite));
  }, [course.id, course.isFavorite]);

  const handleFavoriteClick = async () => {
    if (!isLoggedIn) {
      openAuthPopup();
      return;
    }

    if (favLoading) return;

    const nextFav = !fav;
    setFavLoading(true);

    try {
      if (onToggleFavorite) {
        await onToggleFavorite(nextFav);
      } else if (nextFav) {
        await addWishlistItem(course.id);
      } else {
        await removeWishlistByCourseId(course.id);
      }

      setFav(nextFav);
      onFavoriteChange?.(nextFav);
    } catch {
      setFav(fav);
    } finally {
      setFavLoading(false);
    }
  };

  const courseDetailUrl = ROUTES.COURSE_DETAIL(course.id);
  const teacherProfileUrl = course.instructorId
    ? ROUTES.TEACHER_DETAIL(course.instructorId)
    : null;

  const cardBody = (
    <>
      <div className="flex flex-row items-center justify-between gap-3 text-sm font-medium text-[#666666]">
        <span className="flex items-center gap-1">
          <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          {course.rating}
        </span>
        <span className="flex items-center gap-1">
          <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15.3604 17.2565H17.0774C17.451 17.2565 17.6374 17.2565 17.7936 17.1926C17.9325 17.1357 18.0542 17.0442 18.1473 16.9269C18.2509 16.7931 18.3026 16.6145 18.4053 16.2556L19.3424 12.9768C19.5132 12.3789 19.5978 12.0795 19.5305 11.8439C19.4718 11.637 19.3379 11.4595 19.1551 11.3461C18.9584 11.2244 18.6711 11.2167 18.1197 11.2167M15.3604 17.2565H5.34636M15.3604 17.2565C16.325 17.2565 16.8074 17.2565 17.1758 17.0684C17.5011 16.9027 17.7651 16.6387 17.9308 16.3143C18.1189 15.945 18.1189 15.4618 18.1189 14.4954L18.1197 11.2167M18.1197 11.2167H4.31355L3.09092 15.4963L3.0892 15.5023C2.95114 15.9855 2.86917 16.2729 2.87953 16.4903M18.1197 11.2167L18.1189 7.9379C18.1189 6.97153 18.1189 6.48834 17.9308 6.11905C17.7653 5.79436 17.5013 5.53038 17.1767 5.36493C16.8074 5.17684 16.3242 5.17684 15.3578 5.17684H10.3534M5.34636 17.2565H4.41709C3.79586 17.2565 3.48524 17.2565 3.27643 17.1271C3.09331 17.0138 2.95913 16.8363 2.90024 16.6292C2.88773 16.5842 2.88049 16.5378 2.87866 16.4912M5.34636 17.2565C4.38172 17.2565 3.89853 17.2565 3.5301 17.0684C3.26742 16.9344 3.04343 16.7354 2.87953 16.4903M2.87953 16.4903C2.84157 16.4337 2.807 16.3749 2.77599 16.3143C2.58789 15.945 2.58789 15.4618 2.58789 14.4954V5.17684M10.3534 5.17684H2.58789M10.3534 5.17684L9.43619 4.26051C9.13851 3.96197 8.98924 3.8127 8.81495 3.70571C8.65964 3.6108 8.4934 3.54177 8.31623 3.49863C8.11778 3.45117 7.90639 3.45117 7.48446 3.45117H4.31355C3.85588 3.45117 3.41695 3.63298 3.09333 3.95661C2.7697 4.28023 2.58789 4.71916 2.58789 5.17684"
              stroke="#69991D"
              strokeWidth={1.72566}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {course.chapters} Chapter
        </span>
        <span className="flex items-center gap-1">
          <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id={`lessons-mask-${course.id}`} maskUnits="userSpaceOnUse" x="0" y="0" width="21" height="21" style={{ maskType: 'alpha' }}>
              <rect width="20.708" height="20.708" fill="#D9D9D9" />
            </mask>
            <g mask={`url(#lessons-mask-${course.id})`}>
              <path
                d="M5.82396 18.9829C5.17684 18.9829 4.61959 18.7636 4.15222 18.325C3.68486 17.8864 3.45117 17.3435 3.45117 16.6964V4.65988C3.45117 4.11342 3.62014 3.62449 3.95809 3.19307C4.29603 2.76165 4.73823 2.48842 5.28469 2.37338L13.8052 0.690857V14.4962L5.62982 16.1355C5.5004 16.1643 5.39254 16.2326 5.30626 16.3405C5.21998 16.4483 5.17684 16.567 5.17684 16.6964C5.17684 16.8546 5.24155 16.9876 5.37097 17.0954C5.5004 17.2033 5.65139 17.2572 5.82396 17.2572H15.5308V3.45192H17.2565V18.9829H5.82396ZM6.03967 14.302L7.76533 13.9569V3.64606L6.03967 3.99119V14.302Z"
                fill="#1A87CC"
              />
            </g>
          </svg>
          {course.lessons} Lessons
        </span>
      </div>

      <div className="mt-3 flex flex-row items-center justify-between gap-2">
        <span
          className={`shrink-0 rounded-full px-2.5 py-0.5 text-sm font-medium ${course.tagClass}`}
          style={course.subjectColorCode ? { backgroundColor: course.subjectColorCode } : undefined}
        >
          {course.subject}
        </span>
        <div className="flex min-w-0 flex-1 flex-row items-center justify-end gap-2">
          {teacherProfileUrl ? (
            <Link to={teacherProfileUrl} className="flex min-w-0 flex-row items-center gap-2 transition hover:opacity-80">
              <img src={instructorAvatar} alt="" className="h-[40px] w-[40px] rounded-full object-cover object-top" />
              <span className="truncate text-sm font-medium text-gray-700 hover:text-[#9369CC]">
                {course.instructorName || 'Instructor'}
              </span>
            </Link>
          ) : (
            <>
              <img src={instructorAvatar} alt="" className="h-[40px] w-[40px] rounded-full object-cover" />
              <span className="truncate text-sm font-medium text-gray-700">
                {course.instructorName || 'Instructor'}
              </span>
            </>
          )}
        </div>
      </div>

      <h4 className="mt-4 line-clamp-2 text-[21px] font-bold leading-snug text-[#1B1B1B]">
        <Link to={courseDetailUrl} className="hover:text-[#9369CC]">
          {course.title}
        </Link>
      </h4>
      <p className="mt-2 line-clamp-2 h-auto text-[14px] leading-relaxed text-[#666666]">
        {course.description}
      </p>

      <div className="mt-4">
        <StarRating rating={course.courseRating ?? course.rating} />
      </div>
    </>
  );

  const favoriteButton = (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void handleFavoriteClick();
      }}
      disabled={favLoading}
      className={`absolute z-10 flex h-8 w-8 items-center justify-center rounded-full border-[0.89px] border-white bg-white/90 shadow-sm disabled:opacity-70 right-3 top-3`}
      aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
    >
      <svg
        className={`h-5 w-5 ${fav ? 'fill-[#FF0000] text-[#FF0000]' : 'fill-none text-[#FF0000]'}`}
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
    </button>
  );

  const panelBounceClass =
    panelBounce === 'up'
      ? 'course-panel-bounce-up'
      : panelBounce === 'down'
        ? 'course-panel-bounce-down'
        : '';

  return (
    <article
      className={`relative flex h-full min-h-[460px] flex-col overflow-hidden rounded-2xl bg-white shadow-md ${
        hoverEnroll
          ? 'group transition-shadow duration-300 ease-out hover:shadow-lg'
          : 'transition'
      }`}
      onMouseEnter={() => {
        if (hoverEnroll) setPanelBounce('up');
      }}
      onMouseLeave={() => {
        if (hoverEnroll) setPanelBounce('down');
      }}
    >
      <div
        className={`relative z-0 h-[227px] w-full shrink-0 overflow-hidden px-4 pb-4 pt-3 ${
          course.thumbnailUrl ? '' : `bg-gradient-to-br ${course.gradient}`
        }`}
      >
        {course.thumbnailUrl && (
          <img src={course.thumbnailUrl} alt="" className="absolute inset-0 h-full w-full object-stretch" />
        )}
        <Link to={courseDetailUrl} className="absolute inset-0 z-[1]" aria-label={`View ${course.title}`} />
        {favoriteButton}
      </div>

      {hoverEnroll ? (
        <div
          className={`absolute inset-x-0 bottom-0 top-[227px] z-10 flex flex-col bg-white ${panelBounceClass}`}
        >
          <div className="flex flex-col p-4 pb-2">
            {cardBody}
          </div>
          <div
            className={`mt-auto grid shrink-0 transition-[grid-template-rows] duration-300 ease-out ${
              panelBounce === 'up' ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
          >
            <div className="overflow-hidden">
              <div className="px-4 pb-4 pt-1">
                <Link
                  to={courseDetailUrl}
                  className="inline-flex items-center gap-2 rounded-full bg-[#9369CC] px-5 py-2.5 text-sm font-semibold text-white transition"
                >
                  Enroll Now
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col p-4">{cardBody}</div>
      )}
    </article>
  );
}

export default CourseCard;
